import { clearOrgJoinPending } from "./organization-setup-stub";
import type { SetupTask } from "./user-setup-progress";
import { setupTaskFromLegacyStep } from "./user-setup-progress";

const ONBOARDING_KEY = "lion_pro_signup_onboarding";
const REGISTER_STEP_KEY = "lion_pro_register_step";
const USER_SETUP_STEP_KEY = "lion_pro_user_setup_step";
const USER_SETUP_TASK_KEY = "lion_pro_user_setup_task";

export const ONBOARDING_PATHS = new Set([
  "/register",
  "/user-setup",
  "/verify-email",
  "/auth/google/callback",
]);

export function startSignupOnboarding(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(ONBOARDING_KEY, "1");
  sessionStorage.setItem(REGISTER_STEP_KEY, "1");
}

export function isSignupOnboarding(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(ONBOARDING_KEY) === "1";
}

export function persistRegisterStep(step: number): void {
  if (typeof sessionStorage === "undefined") return;
  if (!isSignupOnboarding()) return;
  sessionStorage.setItem(REGISTER_STEP_KEY, String(Math.min(2, Math.max(1, step))));
}

export function getPersistedRegisterStep(): number | null {
  if (typeof sessionStorage === "undefined") return null;
  if (!isSignupOnboarding()) return null;
  const raw = sessionStorage.getItem(REGISTER_STEP_KEY);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return n >= 1 && n <= 2 ? n : null;
}

export function persistUserSetupTask(task: SetupTask): void {
  if (typeof sessionStorage === "undefined") return;
  if (!isSignupOnboarding()) return;
  sessionStorage.setItem(USER_SETUP_TASK_KEY, task);
  const legacy = task === "email" ? 1 : task === "phone" ? 2 : 3;
  sessionStorage.setItem(USER_SETUP_STEP_KEY, String(legacy));
}

export function getPersistedUserSetupTask(): SetupTask | null {
  if (typeof sessionStorage === "undefined") return null;
  if (!isSignupOnboarding()) return null;

  const taskRaw = sessionStorage.getItem(USER_SETUP_TASK_KEY);
  if (taskRaw === "email" || taskRaw === "phone" || taskRaw === "organization") {
    return taskRaw;
  }

  const raw = sessionStorage.getItem(USER_SETUP_STEP_KEY);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  if (n >= 1 && n <= 4) return setupTaskFromLegacyStep(n);
  return null;
}

/** @deprecated Use persistUserSetupTask */
export function persistUserSetupStep(step: number): void {
  persistUserSetupTask(setupTaskFromLegacyStep(step));
}

/** @deprecated Use getPersistedUserSetupTask */
export function getPersistedUserSetupStep(): number | null {
  const task = getPersistedUserSetupTask();
  if (!task) return null;
  const map: Record<SetupTask, number> = { email: 1, phone: 2, organization: 3 };
  return map[task];
}

/** @deprecated Use persistRegisterStep or persistUserSetupTask */
export function persistSignupStep(step: number): void {
  if (step <= 2) persistRegisterStep(step);
  else persistUserSetupStep(step - 2);
}

/** @deprecated Use getPersistedRegisterStep */
export function getPersistedSignupStep(): number | null {
  return getPersistedRegisterStep();
}

export function clearSignupOnboarding(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(ONBOARDING_KEY);
  sessionStorage.removeItem(REGISTER_STEP_KEY);
  sessionStorage.removeItem(USER_SETUP_STEP_KEY);
  sessionStorage.removeItem(USER_SETUP_TASK_KEY);
  clearOrgJoinPending();
}
